import { Card } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCheckPersonMatchMutation } from '../../../../api/created/personMatchCheck.ts';
import { AddressContactDto, AddressDto } from '../../../../api/generated/model';
import {
  getGetPersonByIdQueryKey,
  useUpdatePerson,
  type IPersonDetails,
  type IPersonListItem,
} from '../../../../api/generated/person-controller/person-controller.ts';
import { checkPersonDuplicate } from '../../../../api/generated/person/checkDuplicate.ts';
import { LeavePageWarning } from '../../../../components/LeavePageWarning.tsx';
import { useStableRef } from '../../../../hooks/useStableRef.ts';
import { getStringOrUndefined, trimOrNull } from '../../../../utils/string.ts';
import { DuplicatePersonConfirmationModal } from '../../components/DuplicatePersonConfirmationModal.tsx';
import {
  DuplicateCheckField,
  DuplicateErrors,
  PersonDataFormFields,
  PersonDataFormValues,
  getDuplicateCheckPayload,
  getDuplicateErrors,
} from '../../components/PersonDataFormFields.tsx';
import { getPersonMatchCheckPayload } from '../../helpers/createPersonData.ts';
import { EditPersonFormHeader } from './EditPersonFormHeader.tsx';
import { usePersonEditFormActions } from './PersonEditFormActionsContext.tsx';

type FormValues = PersonDataFormValues;

export function EditPersonForm({ person }: { person: IPersonDetails }) {
  const queryClient = useQueryClient();
  const [duplicateErrors, setDuplicateErrors] = useState<DuplicateErrors>({});
  const [duplicatePersonData, setDuplicatePersonData] = useState<FormValues | null>(null);
  const [duplicatePersons, setDuplicatePersons] = useState<IPersonListItem[]>([]);
  const { registerActions } = usePersonEditFormActions();
  const personRef = useStableRef(person);

  const updatePersonMutation = useUpdatePerson({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetPersonByIdQueryKey(person.id), data);
      },
      onError: () =>
        toast.error('Bei dem Speichern der Person ist ein Fehler aufgetreten', {
          toastId: 'saveError',
        }),
    },
  });
  const checkPersonMatchMutation = useCheckPersonMatchMutation();

  const {
    register,
    control,
    getFieldState,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty: isFormDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(person),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const clearDuplicateConfirmation = useCallback(() => {
    setDuplicatePersonData(null);
    setDuplicatePersons([]);
  }, []);

  const savePerson = useCallback(
    async (data: FormValues) => {
      toast.dismiss('validationError');
      const savedPerson = await updatePersonMutation.mutateAsync({
        data: {
          id: person.id,
          salutation: data.salutation || undefined,
          firstName: data.firstName,
          lastName: data.lastName,
          birthDate: data.birthDate,
          gender: data.gender,
          mobilePhoneNumber: trimOrNull(data.mobilePhoneNumber) as string,
          privatePhoneNumber: trimOrNull(data.privatePhoneNumber) as string,
          workPhoneNumber: trimOrNull(data.workPhoneNumber) as string,
          email: trimOrNull(data.email) as string,
          nationality: data.nationality ?? null,
          origin: data.origin ?? null,
          arrival: getStringOrUndefined(data.arrival),
          asylumNumber: data.asylumNumber || undefined,
          zemisNumber: data.zemisNumber || undefined,
          languages: data.languages,
          additionalInformation: trimOrNull(data.additionalInformation) as string,
          street: trimOrNull(data.street) as string,
          residencyPermit: data.residencyPermit,
          address: null,
          postalCode: trimOrNull(data.zip) as string,
          city: getStringOrUndefined(data.city),
          state: getStringOrUndefined(data.canton),
          bezugspersonIds: (data.bezugspersonen ?? [])
            .filter((item) => item.isSelected && item.id)
            .map((item) => item.id as string),
          livingStatus: data.livingStatus ?? null,
          note: getStringOrUndefined(data.note),
        },
      });
      clearDuplicateConfirmation();
      reset(getDefaultValues(savedPerson));
    },
    [clearDuplicateConfirmation, person.id, reset, updatePersonMutation],
  );

  const handleFormSubmit = useCallback(
    async (data: FormValues) => {
      updatePersonMutation.reset();
      checkPersonMatchMutation.reset();

      const personMatches = (await checkPersonMatchMutation.mutateAsync(getPersonMatchCheckPayload(data))).filter(
        (match) => match.id !== person.id,
      );

      if (personMatches.length > 0) {
        setDuplicatePersonData(data);
        setDuplicatePersons(personMatches);
        return;
      }

      await savePerson(data);
    },
    [checkPersonMatchMutation, person.id, savePerson, updatePersonMutation],
  );

  const handleConfirmDuplicateSave = useCallback(async () => {
    if (!duplicatePersonData) {
      return;
    }

    updatePersonMutation.reset();
    await savePerson(duplicatePersonData);
  }, [duplicatePersonData, savePerson, updatePersonMutation]);

  const handleSave = useCallback(() => {
    void handleSubmit(handleFormSubmit, () => undefined)();
  }, [handleFormSubmit, handleSubmit]);

  const handleCancel = useCallback(() => {
    reset(getDefaultValues(personRef.current));
    setDuplicateErrors({});
    clearDuplicateConfirmation();
    checkPersonMatchMutation.reset();
    toast.dismiss('validationError');
  }, [checkPersonMatchMutation, clearDuplicateConfirmation, reset]);

  const isSaving = updatePersonMutation.isPending || checkPersonMatchMutation.isPending;
  const isSaveDisabled = !isFormDirty || !isValid || isSaving;

  // The mutation objects are recreated on every render, so handleSave/handleCancel
  // are too. Register stable wrappers instead, otherwise updating the layout
  // actions would re-render this form and re-register endlessly.
  const handleSaveRef = useStableRef(handleSave);
  const handleCancelRef = useStableRef(handleCancel);
  const onSave = useCallback(() => handleSaveRef.current(), [handleSaveRef]);
  const onCancel = useCallback(() => handleCancelRef.current(), [handleCancelRef]);

  useEffect(() => {
    registerActions({
      onSave,
      onCancel,
      isDirty: isFormDirty,
      isSaving,
      isSaveDisabled,
    });

    return () => registerActions(null);
  }, [isFormDirty, isSaveDisabled, isSaving, onCancel, onSave, registerActions]);

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updatePersonMutation.isPending,
    withResolver: true,
  });

  const checkDuplicateField = async (field: DuplicateCheckField, value: string | null | undefined) => {
    const payload = getDuplicateCheckPayload(field, value, getValues());

    if (Object.keys(payload).length === 0) {
      setDuplicateErrors({});
      return;
    }

    try {
      const response = await checkPersonDuplicate(payload);
      setDuplicateErrors(getDuplicateErrors(payload, response, person.id));
    } catch (error) {
      console.error('Duplicate check failed', error);
    }
  };

  return (
    <>
      <Card sx={{ width: '100%', maxWidth: 900 }}>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <EditPersonFormHeader person={person} />
          <PersonDataFormFields
            register={register}
            control={control}
            setValue={setValue}
            getValues={getValues}
            getFieldState={getFieldState}
            errors={errors}
            duplicateErrors={duplicateErrors}
            checkDuplicateField={checkDuplicateField}
            referencePersonSelect={{
              mode: 'edit',
              personId: person.id,
              fetchedOptions: person.bezugspersonen ?? [],
            }}
          />
        </div>
      </Card>

      <DuplicatePersonConfirmationModal
        open={!!duplicatePersonData}
        mode="update"
        loading={updatePersonMutation.isPending}
        persons={duplicatePersons}
        onCancel={clearDuplicateConfirmation}
        onConfirm={handleConfirmDuplicateSave}
      />

      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onDelete={() => blocker.proceed?.()}
        onSave={() => {
          if (blocker.reset) {
            blocker.reset();
          }
          handleSave();
        }}
        title={`Daten von ${person.firstName} ${person.lastName} behalten?`}
      />
    </>
  );
}

function getDefaultValues(person: IPersonDetails): FormValues {
  const personAddress = person.address as {
    address?: AddressDto | null;
    contact?: AddressContactDto | null;
  } | null;

  return {
    salutation: person.salutation ?? '',
    firstName: person.firstName ?? '',
    lastName: person.lastName ?? '',
    birthDate: person.birthDate ?? '',
    gender: person.gender ?? null,
    livingStatus: person.livingStatus ?? null,
    note: person.note ?? '',
    mobilePhoneNumber: person.mobilePhoneNumber ?? '',
    privatePhoneNumber: person.privatePhoneNumber ?? '',
    workPhoneNumber: person.workPhoneNumber ?? '',
    email: person.email ?? '',
    nationality: person.nationality ?? null,
    origin: person.origin ?? null,
    arrival: person.arrival ?? '',
    asylumNumber: person.asylumNumber ?? '',
    zemisNumber: person.zemisNumber ?? '',
    languages: person.languages ?? [],
    bezugspersonen: person.bezugspersonen ?? [],
    residencyPermit: person.residencyPermit ?? null,
    address: personAddress?.address ?? null,
    contact: personAddress?.contact ?? null,
    additionalInformation: person.additionalInformation ?? '',
    street: person.street ?? '',
    zip: person.postalCode != null ? String(person.postalCode) : '',
    city: person.city ?? '',
    canton: person.state ?? '',
  };
}
